import type { RoofLine, RoofLineClass, XY } from "./contract.ts";
import type { InstalledFinishSpec } from "./metalFinish.ts";

const DEFAULT_TOLERANCE_M = 0.002;

export interface FinishTopologySegment {
  class: RoofLineClass;
  start: XY;
  end: XY;
  planeIds: string[];
  startDegree: number;
  endDegree: number;
  startTerminal: boolean;
  endTerminal: boolean;
}

export interface FinishTopologyNode {
  at: XY;
  degree: number;
  classes: RoofLineClass[];
  terminal: boolean;
}

export interface FinishTopology {
  segments: FinishTopologySegment[];
  nodes: FinishTopologyNode[];
  runs: XY[][];
}

export type FlashingKind =
  | "ridge_or_hip_cap"
  | "folded_valley"
  | "eave_drip_and_closure"
  | "rake_or_barge"
  | "stepped_sidewall"
  | "apron"
  | "parapet_cap";

export interface FlashingDescriptor {
  lineClass: RoofLineClass;
  kind: FlashingKind;
  coverM: number;
  upstandM: number;
  homeownerLabel: string;
}

function pointKey(point: XY, toleranceM: number): string {
  return `${Math.round(point[0] / toleranceM)},${Math.round(point[1] / toleranceM)}`;
}

function samePoint(a: XY, b: XY, toleranceM: number): boolean {
  return Math.hypot(a[0] - b[0], a[1] - b[1]) <= toleranceM;
}

function clonePoint(point: XY): XY {
  return [point[0], point[1]];
}

/**
 * Explodes source polylines, computes genuine graph terminals, and groups connected runs.
 * The graph is visual topology only; it never changes measured line lengths or ownership.
 */
export function buildFinishTopology(
  lines: RoofLine[],
  classes: RoofLineClass[],
  toleranceM = DEFAULT_TOLERANCE_M,
): FinishTopology {
  const accepted = new Set(classes);
  const raw = lines
    .filter((line) => accepted.has(line.class))
    .flatMap((line) => line.polyline.slice(0, -1).map((point, index) => ({
      class: line.class,
      start: clonePoint(point),
      end: clonePoint(line.polyline[index + 1]),
      planeIds: [...line.planeIds],
    })))
    .filter((segment) => Math.hypot(segment.end[0] - segment.start[0], segment.end[1] - segment.start[1]) > toleranceM);

  const degree = new Map<string, number>();
  const nodePoint = new Map<string, XY>();
  const nodeClasses = new Map<string, Set<RoofLineClass>>();
  raw.forEach((segment) => {
    [segment.start, segment.end].forEach((point) => {
      const key = pointKey(point, toleranceM);
      degree.set(key, (degree.get(key) ?? 0) + 1);
      nodePoint.set(key, nodePoint.get(key) ?? clonePoint(point));
      const values = nodeClasses.get(key) ?? new Set<RoofLineClass>();
      values.add(segment.class);
      nodeClasses.set(key, values);
    });
  });

  const segments: FinishTopologySegment[] = raw.map((segment) => {
    const startDegree = degree.get(pointKey(segment.start, toleranceM)) ?? 0;
    const endDegree = degree.get(pointKey(segment.end, toleranceM)) ?? 0;
    return {
      ...segment,
      startDegree,
      endDegree,
      startTerminal: startDegree === 1,
      endTerminal: endDegree === 1,
    };
  });

  const unused = new Set(segments.map((_, index) => index));
  const runs: XY[][] = [];
  const takeConnected = (point: XY, excluding: number): number | undefined => {
    for (const index of unused) {
      if (index === excluding) continue;
      const segment = segments[index];
      if (samePoint(segment.start, point, toleranceM) || samePoint(segment.end, point, toleranceM)) return index;
    }
    return undefined;
  };

  while (unused.size) {
    const initialIndex = [...unused].find((index) => segments[index].startTerminal || segments[index].endTerminal) ?? unused.values().next().value;
    if (initialIndex === undefined) break;
    let segment = segments[initialIndex];
    unused.delete(initialIndex);
    let run: XY[] = segment.startTerminal ? [clonePoint(segment.start), clonePoint(segment.end)] : [clonePoint(segment.end), clonePoint(segment.start)];
    let current = run[run.length - 1];
    let guard = segments.length + 1;
    while (guard-- > 0) {
      const nextIndex = takeConnected(current, -1);
      if (nextIndex === undefined) break;
      const next = segments[nextIndex];
      unused.delete(nextIndex);
      const nextPoint = samePoint(next.start, current, toleranceM) ? next.end : next.start;
      run.push(clonePoint(nextPoint));
      current = nextPoint;
      if (samePoint(current, run[0], toleranceM)) break;
    }
    runs.push(run);
  }

  const nodes = [...degree.entries()].map(([key, count]): FinishTopologyNode => ({
    at: clonePoint(nodePoint.get(key)!),
    degree: count,
    classes: [...(nodeClasses.get(key) ?? [])],
    terminal: count === 1,
  }));

  return { segments, nodes, runs };
}

export function flashingDescriptor(lineClass: RoofLineClass, spec: InstalledFinishSpec): FlashingDescriptor {
  if (lineClass === "ridge" || lineClass === "hip") {
    return { lineClass, kind: "ridge_or_hip_cap", coverM: spec.ridgeCoverM, upstandM: spec.ribHeightM, homeownerLabel: lineClass === "ridge" ? "Ridge capping" : "Hip capping" };
  }
  if (lineClass === "valley") {
    return { lineClass, kind: "folded_valley", coverM: spec.valleyWidthM / 2, upstandM: 0.018, homeownerLabel: "Folded valley gutter" };
  }
  if (lineClass === "eave" || lineClass === "gutter") {
    return { lineClass, kind: "eave_drip_and_closure", coverM: 0.055, upstandM: spec.eaveTurnDownM, homeownerLabel: "Eave, drip edge and profile closure" };
  }
  if (lineClass === "rake") {
    return { lineClass, kind: "rake_or_barge", coverM: 0.09, upstandM: 0.105, homeownerLabel: "Barge flashing" };
  }
  if (lineClass === "step_flashing") {
    return { lineClass, kind: "stepped_sidewall", coverM: 0.11, upstandM: 0.11, homeownerLabel: "Stepped sidewall flashing" };
  }
  if (lineClass === "apron_flashing") {
    return { lineClass, kind: "apron", coverM: 0.14, upstandM: 0.11, homeownerLabel: "Apron flashing" };
  }
  return { lineClass, kind: "parapet_cap", coverM: 0.11, upstandM: 0.075, homeownerLabel: "Parapet capping" };
}
